import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from '@phosphor-icons/react';

/**
 * Privacy Policy — placeholder copy. BEFORE PUBLIC LAUNCH: replace every
 * paragraph under `<article>` with legally-binding text from Termly.io,
 * Iubenda, or an attorney. Update EFFECTIVE_DATE and CONTACT_EMAIL below.
 */
const EFFECTIVE_DATE = 'February 1, 2026';
const CONTACT_EMAIL = 'support@thehorseshowsmaster.com';

export default function PrivacyPolicy() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#E5E5E5] py-10 px-4" data-testid="privacy-page">
      <div className="max-w-3xl mx-auto">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-[#9E9E9E] hover:text-white text-sm mb-6 transition-colors"
          data-testid="privacy-back-btn"
        >
          <ArrowLeft size={16} weight="bold" /> Back
        </button>

        <h1 className="text-3xl sm:text-4xl font-black text-white mb-2" style={{ fontFamily: 'Outfit' }}>
          Privacy Policy
        </h1>
        <p className="text-sm text-[#9E9E9E] mb-8">Effective {EFFECTIVE_DATE}</p>

        <article className="space-y-6 text-sm leading-relaxed">
          <section>
            <h2 className="text-lg font-bold text-white mb-2">1. What We Collect</h2>
            <p>Show Master collects the following categories of information:</p>
            <ul className="list-disc list-inside space-y-1 mt-2 text-[#C0C0C0]">
              <li><strong>Account data</strong>: name, email, phone number, role (rider/producer).</li>
              <li><strong>Show data</strong>: show names, dates, locations, classes, results, entries.</li>
              <li><strong>Payment data</strong>: processed and stored by Square — we do not store card numbers. We keep transaction IDs and amounts for history.</li>
              <li><strong>Usage data</strong>: pages visited, actions taken, device and browser info (for analytics and abuse prevention).</li>
              <li><strong>Uploaded content</strong>: facility maps, stall charts, show flyers.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">2. How We Use Your Information</h2>
            <ul className="list-disc list-inside space-y-1 text-[#C0C0C0]">
              <li>Operate the Service (entries, payouts, scoring, notifications).</li>
              <li>Send transactional emails (entry confirmations, payment receipts, results).</li>
              <li>Prevent fraud, abuse, and unauthorized access.</li>
              <li>Analyze usage to improve the Service.</li>
              <li>Comply with legal obligations.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">3. Who We Share It With</h2>
            <p>We share limited data with the following third parties only as needed to operate the Service:</p>
            <ul className="list-disc list-inside space-y-1 mt-2 text-[#C0C0C0]">
              <li><strong>Square</strong> — payment processing (PCI-compliant).</li>
              <li><strong>Resend</strong> — transactional email delivery.</li>
              <li><strong>OpenAI / Anthropic / Google</strong> — AI-based image / map analysis (optional producer tools).</li>
              <li><strong>Producers</strong> — rider name, horse name, payment status, and times for shows you enter.</li>
            </ul>
            <p className="mt-2">We do <strong>not</strong> sell your personal data to advertisers.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">4. Cookies & Tracking</h2>
            <p>
              We use cookies and local storage to keep you logged in and remember your preferences.
              We do not use advertising cookies. You may clear cookies in your browser settings at any time.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">5. Data Retention</h2>
            <p>
              Show and payment records are retained as required for accounting and tax purposes
              (typically 7 years). Account information is retained while your account is active; you
              may request deletion by contacting support.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">6. Your Rights</h2>
            <p>Depending on your jurisdiction, you may have the right to:</p>
            <ul className="list-disc list-inside space-y-1 mt-2 text-[#C0C0C0]">
              <li>Access a copy of the personal data we hold about you.</li>
              <li>Correct inaccurate data.</li>
              <li>Request deletion of your account and associated data.</li>
              <li>Opt out of transactional emails (except service-critical notices).</li>
              <li>Request data portability.</li>
            </ul>
            <p className="mt-2">
              To exercise any of these rights, email{' '}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#0096FF] hover:underline">
                {CONTACT_EMAIL}
              </a>.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">7. Children's Privacy</h2>
            <p>
              Show Master is not intended for children under 13. If you believe we have collected
              information from a child under 13, contact us so we can delete it.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">8. Security</h2>
            <p>
              We use industry-standard measures to protect your data (HTTPS, hashed passwords,
              encrypted-at-rest database, restricted employee access). No system is 100% secure —
              we cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">9. Changes to this Policy</h2>
            <p>
              We may update this Policy from time to time. If we make material changes, we will notify
              registered users by email or via an in-app notice.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">10. Contact</h2>
            <p>
              Questions or concerns? Contact us at{' '}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#0096FF] hover:underline">
                {CONTACT_EMAIL}
              </a>.
            </p>
          </section>

          <section className="bg-[#1a1a1a] border border-[#FF9500]/30 rounded-md p-4 mt-8">
            <p className="text-[11px] text-[#FF9500] font-semibold uppercase tracking-wide mb-1">
              Placeholder Notice
            </p>
            <p className="text-[11px] text-[#9E9E9E]">
              This document is placeholder content. Before public launch, replace with legally-binding
              text from Termly.io, Iubenda, or a licensed attorney. Update the effective date and
              contact email above.
            </p>
          </section>
        </article>
      </div>
    </div>
  );
}
